import { useEffect, useId, useRef, useState } from 'react';
import { X, Copy, Check, Loader2, AlertTriangle, FileText } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { streamScript } from '../services/api';
import type { PromptJob, CliProvider, Engine } from '../services/store';
import type { FlowkitSkillProvenanceData } from '../services/flowkitSkills';
import { FlowkitSkillProvenance } from './FlowkitSkillProvenance';

interface Props {
  job: PromptJob;
  provider: CliProvider;
  engine: Engine;
  hasReferences?: boolean;
  onClose: () => void;
  onJobUpdate: (id: string, status: PromptJob['status'], progress: number, review?: string, script?: string) => void;
}

export default function ScriptOutputPanel({ job, provider, engine, hasReferences, onClose, onJobUpdate }: Props) {
  // A request belongs to the opening action, not subsequent global engine or
  // provider changes. No additional persisted PromptJob ownership is needed.
  const initial = useRef({ job, provider, engine, hasReferences, onJobUpdate });
  const snapshot = initial.current;
  // If job already has a script, start in done/read-only mode
  const [content, setContent] = useState(snapshot.job.script || '');
  const [status, setStatus] = useState<'loading' | 'streaming' | 'done' | 'error'>(
    snapshot.job.script ? 'done' : 'loading'
  );
  const [statusMsg, setStatusMsg] = useState('Initialising…');
  const [copied, setCopied] = useState(false);
  const [provenance, setProvenance] = useState<FlowkitSkillProvenanceData>();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const { job, provider, engine, hasReferences, onJobUpdate } = initial.current;
    if (job.script) return;
    const controller = new AbortController();
    let cancelled = false;
    let started = false, finished = false;
    let accumulated = '', progress = 10;
    const active = () => !cancelled && !controller.signal.aborted;
    // The StrictMode setup/cleanup probe is cancelled before any POST starts.
    // https://react.dev/reference/react/useEffect#my-effect-runs-twice-when-the-component-mounts
    queueMicrotask(() => {
      if (!active()) return;
      started = true;
      onJobUpdate(job.id, 'running', 10);
      void streamScript({ provider, engine, job, hasReferences }, {
        onStatus: text => {
          if (!active()) return;
          setStatusMsg(text);
          if (progress < 30) { progress = 30; onJobUpdate(job.id, 'running', progress); }
        },
        onSkills: data => { if (active()) setProvenance(data); },
        onToken: text => {
          if (!active()) return;
          accumulated += text;
          setContent(accumulated);
          setStatus('streaming');
          progress = Math.min(95, progress + 0.5);
          onJobUpdate(job.id, 'running', Math.round(progress));
        },
        onDone: () => {
          if (!active()) return;
          finished = true;
          setStatus('done');
          onJobUpdate(job.id, 'done', 100, accumulated.slice(0, 120), accumulated);
        },
        onError: message => {
          if (!active()) return;
          finished = true;
          setStatus('error');
          setStatusMsg(message);
          onJobUpdate(job.id, 'error', 0);
        },
      }, controller.signal);
    });
    return () => {
      cancelled = true;
      controller.abort();
      if (started && !finished) {
        // Stopping observation is not proof that the provider cancelled. Do not
        // mark a partial draft done or auto-enqueue another paid request.
        onJobUpdate(job.id, 'error', 0, 'Script observation stopped; provider work may still complete. No automatic retry was submitted.');
      }
    };
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    dialog?.showModal();
    return () => dialog?.close();
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <dialog ref={dialogRef} aria-labelledby={titleId}
      className="m-auto w-[calc(100%_-_2rem)] max-w-4xl h-[85vh] max-h-[90vh] rounded-xl border border-[#d6bd7c] bg-[#fdf6e9] p-0 shadow-2xl backdrop:bg-black/40 backdrop:backdrop-blur-sm"
      onCancel={event => { event.preventDefault(); onClose(); }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="h-full flex flex-col">

        {/* Header */}
        <div className="flex items-center gap-3 border-b border-[#d6bd7c]/60 px-5 py-3 shrink-0">
          <FileText className="w-4 h-4 text-[#865916]" />
          <div className="flex-1 min-w-0">
            <p id={titleId} className="text-xs font-black tracking-[0.06em] text-[#2f2414] truncate">Script: {snapshot.job.idea}</p>
            <p className="text-[10px] text-[#6d5b3f]">{snapshot.job.script ? 'Saved script · Read-only' : `${snapshot.provider.toUpperCase()} · ${snapshot.engine}`} · {snapshot.job.technique} · {snapshot.job.duration}s · {snapshot.job.aspect} · {snapshot.job.language}</p>
          </div>
          <div className="flex items-center gap-2">
            {status === 'done' && (
              <button onClick={handleCopy} className="flex items-center gap-1 rounded-md border border-[#d6bd7c] px-2.5 py-1 text-xs font-bold text-[#6d5b3f] hover:bg-[#e9d6aa]/60 transition-colors">
                {copied ? <><Check className="w-3.5 h-3.5 text-[#476b24]" />Copied</> : <><Copy className="w-3.5 h-3.5" />Copy</>}
              </button>
            )}
            <button onClick={onClose} aria-label="Close script output" className="studio-button-quiet rounded-md p-1.5"><X className="w-4 h-4" /></button>
          </div>
        </div>

        {/* Status bar */}
        {(status === 'loading' || status === 'streaming') && (
          <div role="status" className="flex items-center gap-2 border-b border-[#d6bd7c]/40 bg-[#fff8ec]/60 px-5 py-2 text-xs text-[#6d5b3f] shrink-0">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-[#865916]" />
            {statusMsg}
          </div>
        )}
        {status === 'error' && (
          <div role="alert" className="flex items-center gap-2 border-b border-[#dc553c]/30 bg-[#dc553c]/10 px-5 py-2 text-xs text-[#8c2d1f] shrink-0">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />{statusMsg}
          </div>
        )}

        {/* Script content */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-4">
          {(snapshot.job.script || snapshot.engine === 'flowkit' || snapshot.engine === 'flowboard' || provenance) && (
            <FlowkitSkillProvenance provenance={provenance} notice={snapshot.job.script
              ? 'Saved script: no skill provenance is retained in this view. Its original engine, loaded sources, and hashes cannot be verified. Viewing does not regenerate it.'
              : status === 'loading' || status === 'streaming'
                ? 'Waiting for skill provenance; no sources are claimed as loaded yet.'
                : 'No skill provenance was reported for this request. Loaded sources and hashes cannot be verified.'} />
          )}
          {content ? (
            <div className="prose prose-sm max-w-none text-[#2f2414] [&_h1]:text-[#2f2414] [&_h2]:text-[#2f2414] [&_h3]:text-[#865916] [&_strong]:text-[#2f2414] [&_code]:bg-[#e9d6aa] [&_code]:px-1 [&_code]:rounded [&_blockquote]:border-l-[#865916] [&_blockquote]:text-[#6d5b3f]">
              <ReactMarkdown>{content}</ReactMarkdown>
            </div>
          ) : status === 'loading' ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-[#a89470]">
              <Loader2 className="w-8 h-8 animate-spin" />
              <p className="text-sm font-medium">{statusMsg}</p>
            </div>
          ) : null}
        </div>
      </div>
    </dialog>
  );
}
